"use client";

import { useLocale, useTranslations } from "next-intl";
import { KeyboardEvent, useCallback, useEffect } from "react";
import { Control, Controller, useFormContext, useWatch } from "react-hook-form";

import FormAutoComplete from "@/components/common/forms/FormAutoComplete";
import FormAutoCompleteFullScreen from "@/components/common/forms/FormAutoCompleteFullScreen";
import FormSelect from "@/components/common/forms/FormSelect";
import FormTextField from "@/components/common/forms/FormTextField";
import TextArea from "@/components/ui/TextArea";
import { useUAECarFormData } from "@/hooks/useFormOptions";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getDealDriveBrandsPaginated } from "@/lib/api/get-dealdrive-brands";
import { getDealDriveModelVersionsPaginated } from "@/lib/api/get-dealdrive-model-versions";
import { getDealDriveModelsPaginated } from "@/lib/api/get-dealdrive-models";
import { getDealDriveSpecsPaginated } from "@/lib/api/get-dealdrive-specs";
import { getDealDriveTrimsPaginated } from "@/lib/api/get-dealdrive-trims";
import { cn } from "@/lib/utils";
import { BankValuationSecondStepSchemaType } from "@/schemas/validation/bankValuationSchema";

type MileageUnitValue = "km" | "miles";

interface BankValuationCarDetailsStepProps {
  control: Control<BankValuationSecondStepSchemaType>;
}

const BankValuationCarDetailsStep = ({ control }: BankValuationCarDetailsStepProps) => {
  const { setValue, getValues, setError, clearErrors } = useFormContext<BankValuationSecondStepSchemaType>();
  const isSmallerDevice = useMediaQuery("(max-width: 768px)");
  const t = useTranslations("BankValuation.carDetails");
  const locale = useLocale();
  const isRTL = locale === "ar";

  const make = useWatch({ control, name: "make" });
  const model = useWatch({ control, name: "model" });
  const mileage = useWatch({ control, name: "mileage" });
  const mileageUnit = useWatch({ control, name: "mileageUnit" }) as MileageUnitValue | undefined;

  const mileageUnitValue = mileageUnit ?? "km";

  const mileageUnitToggleOptions = [
    { label: t("mileageUnits.km"), value: "km" as const, ariaLabel: t("mileageUnits.kmAriaLabel") },
    { label: t("mileageUnits.miles"), value: "miles" as const, ariaLabel: t("mileageUnits.milesAriaLabel") },
  ] as const;

  const updateMileageUnit = useCallback(
    (unit: MileageUnitValue) => {
      if (unit === mileageUnitValue) {
        return;
      }

      setValue("mileageUnit", unit, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    },
    [mileageUnitValue, setValue],
  );

  const handleMileageUnitClick = useCallback(
    (unit: MileageUnitValue) => () => {
      updateMileageUnit(unit);
    },
    [updateMileageUnit],
  );

  const handleMileageUnitKeyDown = useCallback(
    (unit: MileageUnitValue) => (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      updateMileageUnit(unit);
    },
    [updateMileageUnit],
  );

  const { yearOptions, colorsOptions, engineTypesOptions } = useUAECarFormData(undefined, {
    language: locale as "en" | "ar" | "ru" | "zh",
  });

  // Sync clientMileage with mileage input
  useEffect(() => {
    if (mileage === undefined) {
      return;
    }

    setValue("clientMileage", mileage === "" ? "" : mileage);
  }, [mileage, setValue]);

  useEffect(() => {
    const values = getValues();
    if (values.make && !values.makeName) {
      getDealDriveBrandsPaginated("", 1).then((response) => {
        const brandOption = response.data.find((item) => item.value === values.make);
        if (brandOption) {
          setValue("makeName", brandOption.label);
        }
      });
    }

    if (values.model && !values.modelName && values.make) {
      getDealDriveModelsPaginated("", 1, { brandId: values.make }).then((response) => {
        const modelOption = response.data.find((item) => item.value === values.model);
        if (modelOption) {
          setValue("modelName", modelOption.label);
        }
      });
    }

    if (values.trim && !values.trimName && values.make && values.model) {
      getDealDriveModelVersionsPaginated("", 1, {
        brandId: values.make,
        modelId: values.model,
      }).then((response) => {
        const versionOption = response.data.find((item) => item.value === values.trim);
        if (versionOption) {
          setValue("trimName", versionOption.label);
          setValue("trim", versionOption.value);
        }
      });
    }

    if (values.trimOption && !values.trimOptionName && values.make && values.model) {
      getDealDriveTrimsPaginated("", 1, {
        categoryModelId: values.model,
      }).then((response) => {
        const trimOptionOption = response.data.find((item) => item.value === values.trimOption);
        if (trimOptionOption) {
          setValue("trimOptionName", trimOptionOption.label);
          setValue("trimOption", trimOptionOption.value);
        }
      });
    }

    if (values.specification && !values.specs) {
      getDealDriveSpecsPaginated("", 1).then((response) => {
        const specOption = response.data.find((item) => item.value === values.specification);
        if (specOption) {
          setValue("specs", specOption.label);
        }
      });
    }
  }, [getValues, setValue]);

  const loadMakesOptions = useCallback(async (inputValue: string, page?: number) => {
    try {
      const response = await getDealDriveBrandsPaginated(inputValue, page);
      return response;
    } catch (error) {
      console.error("Error loading makes:", error);
      return {
        data: [],
        meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
      };
    }
  }, []);

  const loadModelsOptions = useCallback(
    async (inputValue: string, page?: number) => {
      if (!make) {
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
      try {
        const response = await getDealDriveModelsPaginated(inputValue, page, { brandId: make });
        return response;
      } catch (error) {
        console.error("Error loading models:", error);
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
    },
    [make],
  );

  const loadModelVersionsOptions = useCallback(
    async (inputValue: string, page?: number) => {
      if (!make || !model) {
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
      try {
        const response = await getDealDriveModelVersionsPaginated(inputValue, page, {
          brandId: make,
          modelId: model,
        });

        const currentTrim = getValues("trim");

        if (response.data && response.data.length > 0 && (!currentTrim || currentTrim === "")) {
          setError("trim", {
            type: "manual",
            message: "Trim is required when available",
          });
        } else if (currentTrim && currentTrim !== "") {
          clearErrors("trim");
        }

        return response;
      } catch (error) {
        console.error("Error loading model versions:", error);
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
    },
    [make, model, setError, clearErrors, getValues],
  );

  const loadTrimOptions = useCallback(
    async (inputValue: string, page?: number) => {
      if (!make || !model) {
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
      try {
        const response = await getDealDriveTrimsPaginated(inputValue, page, {
          categoryModelId: model,
        });

        return response;
      } catch (error) {
        console.error("Error loading trims:", error);
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
    },
    [make, model],
  );

  const loadSpecificationsOptions = useCallback(async (inputValue: string, page?: number) => {
    try {
      const response = await getDealDriveSpecsPaginated(inputValue, page);
      return response;
    } catch (error) {
      console.error("Error loading specifications:", error);
      return {
        data: [],
        meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
      };
    }
  }, []);

  // Helper functions for no options messages
  const getModelNoOptionsMessage = useCallback(() => {
    return make ? t("noModelsFound") : t("selectMakeFirst");
  }, [make, t]);

  const getTrimNoOptionsMessage = useCallback(() => {
    if (!make) return t("selectMakeFirst");
    if (!model) return t("selectModelFirst");
    return t("noVersionsFound");
  }, [make, model, t]);

  const getModelOptionsNoOptionsMessage = useCallback(() => {
    if (!make) return t("selectMakeFirst");
    if (!model) return t("selectModelFirst");
    return t("noOptionsFound");
  }, [make, model, t]);

  return (
    <div className="mt-10">
      <div className="mt-4">
        <div className="mt-10">
          <h4 className="mb-4 font-inter text-base font-bold text-primary-foreground">{t("title")}</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isSmallerDevice ? (
              <FormAutoCompleteFullScreen
                isClearable
                name="make"
                helperText={t("makes")}
                control={control}
                placeholder={t("make")}
                loadOptions={loadMakesOptions}
                onSelect={(option) => {
                  if (option) {
                    setValue("make", option.value);
                    setValue("makeName", option.label);
                    setValue("model", "");
                    setValue("modelName", "");
                    setValue("trim", "");
                    setValue("trimName", "");
                  }
                }}
                defaultOptions={true}
                focusSearchBarOnOpen
                autoSubmit
              />
            ) : (
              <FormAutoComplete
                name="make"
                control={control}
                placeholder={t("make")}
                loadOptions={loadMakesOptions}
                onSelect={(option) => {
                  if (option) {
                    setValue("make", option.value);
                    setValue("makeName", option.label);
                    setValue("model", "");
                    setValue("modelName", "");
                    setValue("trim", "");
                    setValue("trimName", "");
                  }
                }}
                defaultOptions={true}
              />
            )}
            {isSmallerDevice
              ? make && (
                  <FormAutoCompleteFullScreen
                    name="model"
                    helperText={t("models")}
                    control={control}
                    placeholder={t("model")}
                    loadOptions={loadModelsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("model", option.value);
                        setValue("modelName", option.label);
                        setValue("trim", "");
                        setValue("trimName", "");
                      }
                    }}
                    autoSubmit
                    dependencies={{ brand: make }}
                    defaultOptions={!!make}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={getModelNoOptionsMessage}
                    focusSearchBarOnOpen
                  />
                )
              : make && (
                  <FormAutoComplete
                    name="model"
                    control={control}
                    placeholder={t("model")}
                    loadOptions={loadModelsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("model", option.value);
                        setValue("modelName", option.label);
                        setValue("trim", "");
                        setValue("trimName", "");
                      }
                    }}
                    dependencies={{ brand: make }}
                    defaultOptions={!!make}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={getModelNoOptionsMessage}
                  />
                )}

            {isSmallerDevice
              ? make &&
                model && (
                  <FormAutoCompleteFullScreen
                    name="trim"
                    helperText={t("trims")}
                    control={control}
                    placeholder={t("trim")}
                    loadOptions={loadModelVersionsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("trim", option.value);
                        setValue("trimName", option.label);
                      }
                    }}
                    autoSubmit
                    dependencies={{ brand: make, model }}
                    defaultOptions={!!(make && model)}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={getTrimNoOptionsMessage}
                    hideIfNoData
                    focusSearchBarOnOpen
                  />
                )
              : make &&
                model && (
                  <FormAutoComplete
                    name="trim"
                    control={control}
                    placeholder={t("trim")}
                    loadOptions={loadModelVersionsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("trim", option.value);
                        setValue("trimName", option.label);
                      }
                    }}
                    dependencies={{ brand: make, model }}
                    defaultOptions={!!(make && model)}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={getTrimNoOptionsMessage}
                    hideIfNoData
                  />
                )}

            {isSmallerDevice
              ? make &&
                model && (
                  <FormAutoCompleteFullScreen
                    name="trimOption"
                    helperText={t("modelOptions")}
                    control={control}
                    placeholder={t("modelOptions")}
                    loadOptions={loadTrimOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("trimOption", option.value);
                        setValue("trimOptionName", option.label);
                      }
                    }}
                    autoSubmit
                    dependencies={{ brand: make, model }}
                    defaultOptions={!!(make && model)}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={getModelOptionsNoOptionsMessage}
                    hideIfNoData
                    focusSearchBarOnOpen
                  />
                )
              : make &&
                model && (
                  <FormAutoComplete
                    name="trimOption"
                    control={control}
                    placeholder={t("modelOptions")}
                    loadOptions={loadTrimOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("trimOption", option.value);
                        setValue("trimOptionName", option.label);
                      }
                    }}
                    dependencies={{ brand: make, model }}
                    defaultOptions={!!(make && model)}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={getModelOptionsNoOptionsMessage}
                    hideIfNoData
                  />
                )}

            <FormSelect<BankValuationSecondStepSchemaType>
              name="year"
              control={control}
              placeholder={t("year")}
              options={yearOptions}
            />
            <div className="relative">
              <FormTextField
                name="mileage"
                className="min-h-[58px] pe-32 placeholder:text-base"
                control={control}
                placeholder={t("mileage")}
                type="number"
                formatNumber
                required
                containerClassName="w-full relative"
              />
              <div className="z-1 absolute end-3 top-[29px] flex -translate-y-1/2 overflow-hidden rounded-xl border border-secondary-light bg-white p-1 shadow-sm">
                <div
                  className={cn(
                    mileageUnitValue === "miles" ? "w-[calc(50%-0.25rem)]" : "w-[calc(50%-0.5rem)]",
                    "pointer-events-none absolute bottom-1 top-1 z-0 rounded-lg bg-primary transition-transform duration-100 ease-in-out",
                  )}
                  style={{
                    [isRTL ? "right" : "left"]: "0.25rem",
                    transform: isRTL
                      ? mileageUnitValue === "miles"
                        ? "translateX(-95%)"
                        : "translateX(0)"
                      : mileageUnitValue === "miles"
                        ? "translateX(95%)"
                        : "translateX(0)",
                  }}
                />
                {mileageUnitToggleOptions.map(({ label, value, ariaLabel }, idx) => {
                  const isActive = mileageUnitValue === value;
                  const isLeft = idx === 0;

                  return (
                    <button
                      key={value}
                      type="button"
                      tabIndex={0}
                      aria-label={ariaLabel}
                      aria-pressed={isActive}
                      className={cn(
                        "relative z-[1] flex-1 rounded-lg px-3 py-1 text-xs font-semibold uppercase transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                        isActive ? "text-white" : "text-secondary-foreground",
                        !isActive && "hover:text-primary",
                        isLeft && "rounded-e-none",
                        !isLeft && "rounded-s-none",
                      )}
                      onClick={handleMileageUnitClick(value)}
                      onKeyDown={handleMileageUnitKeyDown(value)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <FormSelect<BankValuationSecondStepSchemaType>
              name="engineType"
              control={control}
              placeholder={t("engineType")}
              options={engineTypesOptions}
              required
            />

            <FormSelect<BankValuationSecondStepSchemaType>
              name="color"
              control={control}
              placeholder={t("color")}
              options={colorsOptions}
            />

            <FormTextField
              name="vin"
              className="min-h-[58px] placeholder:text-base"
              control={control}
              placeholder={t("vinNumber")}
              type="text"
              required
            />

            <FormTextField
              name="engineNumber"
              className="min-h-[58px] placeholder:text-base"
              control={control}
              placeholder={t("engineNumber")}
              type="text"
              aria-label={t("engineNumber")}
            />

            <FormAutoComplete
              name="specification"
              control={control}
              placeholder={t("specifications")}
              loadOptions={loadSpecificationsOptions}
              onSelect={(option) => {
                if (option) {
                  setValue("specification", option.value);
                  setValue("specs", option.label);
                }
              }}
              defaultOptions={true}
            />
          </div>

          <div className="mt-4 lg:col-span-3">
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextArea
                    rows={5}
                    placeholder={t("notes")}
                    className="inter w-full bg-secondary-light text-base placeholder:text-base placeholder:text-secondary-foreground"
                    {...field}
                  />
                  {error && <p className="inter text-sm text-red-500">{error.message}</p>}
                </>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankValuationCarDetailsStep;
